import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { fmtNumber, relativeTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Play, Pause, ArrowLeft, Pencil, Save, X, Plus, Trash2, Zap, Users, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";
import { CampaignContactImport } from "@/components/CampaignContactImport";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

export default function CampaignDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [enr, setEnr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [segs, setSegs] = useState([]);
  const [sourcing, setSourcing] = useState(false);
  const [quickEmail, setQuickEmail] = useState("");
  const [addingEmail, setAddingEmail] = useState(false);
  const [networks, setNetworks] = useState(null);
  const [netLoading, setNetLoading] = useState(false);
  const [importingNet, setImportingNet] = useState("");
  const navigate = useNavigate();
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneForm, setCloneForm] = useState({ name: "", replace_from: "SEAJET", replace_to: "" });
  const [cloning, setCloning] = useState(false);

  const openClone = () => {
    setCloneForm({ name: "", replace_from: "SEAJET", replace_to: "" });
    setCloneOpen(true);
  };
  const doClone = async () => {
    const to = cloneForm.replace_to.trim();
    setCloning(true);
    try {
      const { data } = await api.post(`/campaigns/${id}/clone`, {
        name: cloneForm.name.trim() || undefined,
        replace_from: cloneForm.replace_from.trim() || undefined,
        replace_to: to || undefined,
      });
      toast.success(`Cloned to "${data.name}" — opening it now`);
      setCloneOpen(false);
      navigate(`/app/campaigns/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
    setCloning(false);
  };

  const loadNetworks = async () => {
    setNetLoading(true);
    try {
      const { data } = await api.get("/campaigns/directory-networks");
      setNetworks(data.networks || []);
    } catch (e) {
      const err = e?.response?.data?.detail || "";
      toast.error(err === "directory_not_configured"
        ? "Connect your partner directory in Settings first."
        : (err || "Could not load networks"));
      setNetworks([]);
    }
    setNetLoading(false);
  };

  const importNetwork = async (network) => {
    setImportingNet(network);
    try {
      const { data } = await api.post(`/campaigns/${id}/import-network`, { network });
      toast.success(`Imported ${data.created} ${network} agents into this campaign`, {
        description: `${data.enrolled} enrolled${data.duplicates ? `, ${data.duplicates} already present` : ""}. They'll start once the campaign is Active.`,
        duration: 8000,
      });
      load(); loadEnr();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    }
    setImportingNet("");
  };

  const load = () => api.get(`/campaigns/${id}`).then((r) => setC(r.data));
  const loadEnr = () => api.get(`/campaigns/${id}/enrollments`).then((r) => setEnr(r.data)).catch(() => {});
  const loadSegs = () => api.get(`/campaigns/segment-counts`).then((r) => setSegs(r.data.segments || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load(); loadEnr(); loadSegs();
    const t = setInterval(() => { load(); loadEnr(); }, 15000); // live refresh as steps fire
    return () => clearInterval(t);
  }, [id]);

  const sourceAudience = async (mode, segment, label) => {
    setSourcing(true);
    try {
      await api.post(`/campaigns/${id}/source`, { mode, segment });
      toast.success(
        mode === "ai" ? "Finding net-new prospects with AI…" : `Enrolling your ${label || "prospects"}…`,
        { description: "This runs in the background — the audience will populate within ~30s." });
      setTimeout(() => { load(); loadEnr(); }, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not source prospects"); }
    setSourcing(false);
  };

  const addSingleEmail = async () => {
    const email = quickEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email address");
      return;
    }
    setAddingEmail(true);
    try {
      const { data } = await api.post(`/campaigns/${id}/import-contacts`, { contacts: [{ email }] });
      if (data.enrolled > 0) {
        toast.success(`${email} added to this campaign`, { description: "Enrolled into the marketing flow." });
      } else if (data.duplicates > 0 || data.reused > 0) {
        toast.info(`${email} was already in this campaign`);
      } else {
        toast.success(`${email} added`);
      }
      setQuickEmail("");
      load(); loadEnr();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add email"); }
    setAddingEmail(false);
  };

  const toggleStatus = async () => {
    const next = c.status === "active" ? "paused" : "active";
    await api.patch(`/campaigns/${id}`, { status: next });
    toast.success(`Campaign ${next}`);
    load();
  };

  const runNow = async () => {
    try {
      const { data } = await api.post(`/campaigns/${id}/run-now`);
      toast.success(`Processing ${data.nudged} contacts now`, {
        description: "Email steps auto-send (no approval needed). Watch the Inbox → Sent tab and Activity feed within ~2 min.",
      });
      setTimeout(() => { load(); loadEnr(); }, 2500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not run now"); }
  };

  const startEdit = () => {
    setDraft({
      name: c.name || "",
      sequence_steps: (c.sequence_steps || []).map((s) => ({ ...s, _uid: uid() })),
      safety_settings: { ...(c.safety_settings || {}) },
    });
    setEditing(true);
  };

  const updateStep = (i, k, v) => {
    const steps = [...draft.sequence_steps];
    steps[i] = { ...steps[i], [k]: v };
    setDraft({ ...draft, sequence_steps: steps });
  };
  const addStep = () => setDraft({
    ...draft,
    sequence_steps: [...draft.sequence_steps,
      { step: draft.sequence_steps.length + 1, channel: "email", action: "follow_up", delay_days: 7, template: "", _uid: uid() }],
  });
  const removeStep = (i) => setDraft({
    ...draft,
    sequence_steps: draft.sequence_steps.filter((_, idx) => idx !== i).map((s, idx) => ({ ...s, step: idx + 1 })),
  });
  const updateSafety = (k, v) => setDraft({ ...draft, safety_settings: { ...draft.safety_settings, [k]: v } });

  const save = async () => {
    if (!draft.name.trim()) { toast.error("Campaign name is required"); return; }
    setSaving(true);
    try {
      await api.patch(`/campaigns/${id}`, {
        name: draft.name.trim(),
        sequence_steps: draft.sequence_steps.map((s) => ({
          step: s.step, channel: s.channel, action: s.action,
          delay_days: parseInt(s.delay_days || 0), template: s.template || "",
        })),
        safety_settings: draft.safety_settings,
      });
      toast.success("Campaign saved");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save campaign");
    }
    setSaving(false);
  };

  if (!c) return <DashboardLayout title="..."><div className="text-sm text-muted-foreground">Loading...</div></DashboardLayout>;

  const steps = editing ? draft.sequence_steps : (c.sequence_steps || []);

  return (
    <DashboardLayout
      eyebrow={c.target_segment}
      title={editing ? "Editing campaign" : c.name}
      actions={
        <div className="flex items-center gap-2">
          <Link to="/app/campaigns" data-testid="campaign-back-link" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mr-2">
            <ArrowLeft className="w-3 h-3" /> Back
          </Link>
          {editing ? (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)} data-testid="campaign-cancel-btn" className="rounded-sm">
                <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
              </Button>
              <Button onClick={save} disabled={saving} data-testid="campaign-save-btn" className="rounded-sm">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={startEdit} data-testid="campaign-edit-btn" className="rounded-sm">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
              <Button variant="outline" onClick={openClone} data-testid="campaign-clone-btn" className="rounded-sm">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Clone
              </Button>
              {c.status === "active" && (
                <Button variant="outline" onClick={runNow} data-testid="campaign-run-now-btn" className="rounded-sm">
                  <Zap className="w-3.5 h-3.5 mr-1.5" /> Run now
                </Button>
              )}
              <Button onClick={toggleStatus} data-testid="campaign-toggle-btn" className="rounded-sm">
                {c.status === "active" ? <><Pause className="w-3.5 h-3.5 mr-1.5" />Pause</> : <><Play className="w-3.5 h-3.5 mr-1.5" />Activate</>}
              </Button>
            </>
          )}
        </div>
      }
    >
      {editing && (
        <div className="data-card mb-6">
          <Label className="text-xs">Campaign name</Label>
          <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="mt-1" data-testid="campaign-name-input" />
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 grid-borders bg-card/30">
        <Stat label="Status" value={c.status} />
        <Stat label="Audience" value={fmtNumber(c.audience_count || 0)} />
        <Stat label="Sent" value={fmtNumber(c.metrics?.sent)} />
        <Stat label="Accepted" value={fmtNumber(c.metrics?.accepted)} />
        <Stat label="Replied" value={fmtNumber(c.metrics?.replied)} />
      </div>

      {!editing && (
        <div className="data-card mt-6" data-testid="campaign-audience-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-eyebrow">Audience &amp; progress</div>
              <h3 className="font-display font-semibold text-lg">
                {c.audience_status === "sourcing" ? "Sourcing audience from this campaign's ICP…"
                  : c.audience_status === "sourced" ? `${enr?.total ?? c.audience_count ?? 0} prospects enrolled`
                  : "Not sourced yet"}
              </h3>
            </div>
            {enr?.by_status && (
              <div className="flex flex-wrap gap-1.5 justify-end max-w-xs">
                {Object.entries(enr.by_status).map(([k, v]) => (
                  <span key={k} className="signal-pill" data-testid={`enr-status-${k}`}>{k.replace(/_/g, " ")}: {v}</span>
                ))}
              </div>
            )}
          </div>
          <div className="mb-4 space-y-4">
            <div className="border border-signal-500/30 bg-signal-500/5 rounded-sm p-4 space-y-3" data-testid="campaign-add-prospects">
              <div>
                <span className="text-[0.65rem] tracking-widest uppercase text-signal-500 font-semibold">Add prospects to this campaign</span>
                <p className="text-[0.7rem] text-muted-foreground mt-1">
                  No CSV needed — enroll prospects you already have, or let the AI find net-new targets from this campaign's ICP.
                </p>
              </div>
              <div data-testid="campaign-quick-add-email">
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mb-1.5">Add a single email</div>
                <div className="flex items-center gap-2">
                  <Input
                    type="email"
                    value={quickEmail}
                    onChange={(e) => setQuickEmail(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSingleEmail(); } }}
                    placeholder="name@company.com"
                    disabled={addingEmail}
                    data-testid="quick-add-email-input"
                    className="h-9 text-sm" />
                  <Button size="sm" onClick={addSingleEmail} disabled={addingEmail || !quickEmail.trim()}
                    data-testid="quick-add-email-btn" className="rounded-sm shrink-0">
                    <Plus className="w-3.5 h-3.5 mr-1" />{addingEmail ? "Adding…" : "Add"}
                  </Button>
                </div>
                <p className="text-[0.7rem] text-muted-foreground mt-1.5">
                  Instantly enrolls this address into the campaign's marketing flow (email sequence).
                </p>
              </div>

              <div data-testid="campaign-network-import">
                <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mb-1.5">Import a partner network</div>
                {networks === null ? (
                  <Button size="sm" variant="outline" onClick={loadNetworks} disabled={netLoading}
                    data-testid="load-networks-btn" className="rounded-sm">
                    <Users className="w-3.5 h-3.5 mr-1.5" />{netLoading ? "Loading networks…" : "Load networks from directory"}
                  </Button>
                ) : networks.length === 0 ? (
                  <p className="text-[0.7rem] text-muted-foreground">No networks found. Connect your partner directory in Settings.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {networks.map((n) => (
                      <button key={n.network} type="button" disabled={!!importingNet}
                        onClick={() => importNetwork(n.network)}
                        data-testid={`import-network-${n.network}`}
                        className="flex items-center justify-between rounded-sm border border-border hover:border-signal-500/60 px-3 py-2 text-left transition-colors disabled:opacity-50">
                        <div>
                          <div className="text-sm font-medium">{n.network}</div>
                          <div className="text-[0.65rem] text-muted-foreground">{n.companies} companies · {n.contacts} contacts</div>
                        </div>
                        {importingNet === n.network
                          ? <Loader2 className="w-4 h-4 animate-spin text-signal-500" />
                          : <Plus className="w-4 h-4 text-signal-500" />}
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-[0.7rem] text-muted-foreground mt-1.5">
                  Pulls a whole network's agents (e.g. Seajet) from your partner directory and enrolls them. Nothing sends until the campaign is Active.
                </p>
              </div>
              {segs.some((s) => s.count > 0) && (
                <div>
                  <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mb-1.5">Enroll prospects you already have</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {segs.filter((s) => s.count > 0).map((s) => (
                      <button key={s.id} type="button" disabled={sourcing}
                        data-testid={`source-segment-${s.id}`}
                        onClick={() => sourceAudience("segment", s.id, s.label)}
                        className="text-left px-3 py-2 rounded-sm border border-border hover:border-signal-500 hover:bg-signal-500/10 transition-colors disabled:opacity-50">
                        <div className="text-sm font-medium">{s.label}</div>
                        <div className="text-[0.7rem] text-muted-foreground">{s.count} contact{s.count === 1 ? "" : "s"} available</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Button variant="outline" size="sm" disabled={sourcing} onClick={() => sourceAudience("ai")}
                  data-testid="source-ai-btn" className="rounded-sm">
                  <Zap className="w-3.5 h-3.5 mr-1.5" /> Find net-new prospects with AI
                </Button>
                <p className="text-[0.7rem] text-muted-foreground mt-1.5">
                  AI generates companies + contacts from this campaign's ICP. Note: AI leads often need email enrichment (Apollo) before the email steps can send — LinkedIn steps work once resolved.
                </p>
              </div>
            </div>
            <CampaignContactImport campaignId={id} onImported={() => { load(); loadEnr(); }} />
            <p className="text-[0.7rem] text-muted-foreground mt-1.5">
              Or upload a contact list (e.g. your freight-network agents). Columns: Company Name, Country, City, Contact Name, Email, Role, Website, Services.
            </p>
          </div>
          {enr?.enrollments?.length ? (
            <ul className="divide-y divide-border/60 text-sm">
              {enr.enrollments.slice(0, 8).map((e) => (
                <li key={e.id} className="py-2 flex items-center justify-between gap-3" data-testid={`enr-row-${e.id}`}>
                  <div className="min-w-0">
                    <span className="font-medium">{e.prospect?.full_name || "—"}</span>
                    <span className="text-muted-foreground"> · {e.prospect?.company || ""}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-xs">
                    <span className="text-muted-foreground">step {Math.min((e.current_step || 0) + 1, (c.sequence_steps || []).length)}/{(c.sequence_steps || []).length}</span>
                    <span className="signal-pill">{(e.status || "").replace(/_/g, " ")}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              {c.audience_status === "sourcing" ? "Hang tight — generating + enrolling leads (this can take ~30s)." : "No prospects enrolled yet."}
            </p>
          )}
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="data-card lg:col-span-2">
          <div className="label-eyebrow mb-2">Sequence</div>
          <h3 className="font-display font-semibold text-lg mb-5">{steps.length}-step cadence</h3>

          {editing ? (
            <div className="space-y-4">
              {steps.map((s, i) => (
                <div key={s._uid || i} className="border border-border rounded-sm p-4" data-testid={`campaign-edit-step-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">Step {s.step}</span>
                    {steps.length > 1 && (
                      <button onClick={() => removeStep(i)} className="text-muted-foreground hover:text-destructive" data-testid={`campaign-remove-step-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <div>
                      <Label className="text-xs">Delay (days)</Label>
                      <Input type="number" value={s.delay_days} onChange={(e) => updateStep(i, "delay_days", parseInt(e.target.value || "0"))} data-testid={`campaign-delay-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Channel</Label>
                      <Input value={s.channel} onChange={(e) => updateStep(i, "channel", e.target.value)} data-testid={`campaign-channel-${i}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Action</Label>
                      <Input value={s.action} onChange={(e) => updateStep(i, "action", e.target.value)} data-testid={`campaign-action-${i}`} />
                    </div>
                  </div>
                  <Label className="text-xs">Template</Label>
                  <Textarea rows={6} value={s.template} onChange={(e) => updateStep(i, "template", e.target.value)} data-testid={`campaign-template-${i}`} className="font-mono text-xs" />
                </div>
              ))}
              <Button variant="outline" onClick={addStep} className="w-full rounded-sm" data-testid="campaign-add-step-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add step
              </Button>
            </div>
          ) : (
            <ol className="space-y-3">
              {steps.map((s, i) => (
                <li key={i} className="border-l-2 border-signal-500 pl-4 py-2" data-testid={`campaign-step-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[0.65rem] tracking-widest uppercase text-muted-foreground">Step {s.step}</span>
                    <span className="signal-pill">{s.channel}</span>
                    <span className="signal-pill">{s.action}</span>
                    <span className="text-xs text-muted-foreground ml-auto">+{s.delay_days}d</span>
                  </div>
                  <pre className="text-xs whitespace-pre-wrap text-muted-foreground font-mono">{s.template}</pre>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="data-card">
          <div className="label-eyebrow mb-2">Meta</div>
          {editing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Daily invite limit</Label>
                <Input type="number" value={draft.safety_settings?.daily_invite_limit ?? ""} onChange={(e) => updateSafety("daily_invite_limit", parseInt(e.target.value || "0"))} data-testid="campaign-invite-limit" />
              </div>
              <div>
                <Label className="text-xs">Min gap (seconds)</Label>
                <Input type="number" value={draft.safety_settings?.min_gap_seconds ?? ""} onChange={(e) => updateSafety("min_gap_seconds", parseInt(e.target.value || "0"))} data-testid="campaign-min-gap" />
              </div>
              <div>
                <Label className="text-xs">Randomize (%)</Label>
                <Input type="number" value={draft.safety_settings?.randomize_pct ?? ""} onChange={(e) => updateSafety("randomize_pct", parseInt(e.target.value || "0"))} data-testid="campaign-randomize" />
              </div>
            </div>
          ) : (
            <dl className="text-sm space-y-2">
              <Row k="Created" v={relativeTime(c.created_at)} />
              <Row k="Invite limit" v={c.safety_settings?.daily_invite_limit ?? "—"} />
              <Row k="Min gap (s)" v={c.safety_settings?.min_gap_seconds ?? "—"} />
              <Row k="Randomize" v={`${c.safety_settings?.randomize_pct ?? 0}%`} />
              <Row k="Meetings" v={fmtNumber(c.metrics?.meetings)} />
            </dl>
          )}
        </div>
      </div>

      <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <DialogContent className="max-w-md" data-testid="campaign-clone-dialog">
          <DialogHeader>
            <DialogTitle>Clone campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-[0.72rem] text-muted-foreground">
              Makes a fresh copy (no enrolled audience, zero stats). Optionally swap a word everywhere in the copy — e.g. turn "SEAJET" into "PANGEA".
            </p>
            <div>
              <Label className="text-xs">New campaign name <span className="text-muted-foreground">(optional)</span></Label>
              <Input value={cloneForm.name} onChange={(e) => setCloneForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Europacific – PANGEA Network" className="mt-1" data-testid="clone-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Replace word</Label>
                <Input value={cloneForm.replace_from} onChange={(e) => setCloneForm((f) => ({ ...f, replace_from: e.target.value }))}
                  placeholder="SEAJET" className="mt-1" data-testid="clone-from-input" />
              </div>
              <div>
                <Label className="text-xs">With</Label>
                <Input value={cloneForm.replace_to} onChange={(e) => setCloneForm((f) => ({ ...f, replace_to: e.target.value }))}
                  placeholder="PANGEA" className="mt-1" data-testid="clone-to-input" />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["PANGEA", "CONNECTA", "WFN", "XLP", "TWIG"].map((n) => (
                <button key={n} type="button" data-testid={`clone-preset-${n.toLowerCase()}`}
                  onClick={() => setCloneForm((f) => ({ ...f, replace_to: n, name: `Europacific – ${n} Network` }))}
                  className="text-[0.7rem] px-2 py-1 rounded-sm border border-border hover:bg-muted transition-colors">
                  {n}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneOpen(false)} disabled={cloning}>Cancel</Button>
            <Button onClick={doClone} disabled={cloning} data-testid="clone-confirm-btn">
              {cloning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cloning…</> : "Create clone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-6">
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="text-3xl font-display font-semibold tracking-tightest capitalize">{value}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between"><span className="text-muted-foreground">{k}</span><span>{v}</span></div>
  );
}
